import { Directive, ElementRef, HostListener, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: '[sgtNovoCnpjCpf]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NovoCnpjCpfDirective),
      multi: true,
    },
  ],
})
export class NovoCnpjCpfDirective implements ControlValueAccessor {
  private readonly MAX_DIGITS_CPF = 11;
  private readonly MAX_DIGITS_CNPJ = 14;
  private readonly SEPARATORS = ['.', '-', '/'];
  private readonly DIGIT_REGEX = /\d/;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private el: ElementRef<HTMLInputElement>) {}

  writeValue(value: string): void {
    const digits = this.extractDigits(value ?? '');
    this.el.nativeElement.value = this.format(digits);
  }

  registerOnChange(fn: (value: string) => void): void {
 this.onChange = fn;
}
  registerOnTouched(fn: () => void): void {
 this.onTouched = fn;
}
  setDisabledState(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled;
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;
    const oldValue = input.value;

    const digitsBeforeCursor = this.countDigitsBeforePosition(oldValue, cursorPosition);
    const rawValue = this.extractDigits(oldValue);
    const formatted = this.format(rawValue);
    const newCursorPos = this.findCursorPosition(formatted, digitsBeforeCursor);

    input.value = formatted;
    this.setCursorPosition(input, newCursorPos);

    this.onChange(rawValue);
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const input = this.el.nativeElement;
    const cursorPosition = input.selectionStart || 0;

    if (this.shouldBlockInput(event, input)) {
      event.preventDefault();
      return;
    }

    this.handleBackspace(event, input, cursorPosition);
  }

  private handleBackspace(
    event: KeyboardEvent,
    input: HTMLInputElement,
    cursorPosition: number
  ): void {
    if (event.key !== 'Backspace' || cursorPosition === 0) {
      return;
    }

    const charBeforeCursor = input.value[cursorPosition - 1];
    if (!this.isSeparator(charBeforeCursor)) {
      return;
    }

    event.preventDefault();
    const newPos = this.findPreviousDigitPosition(input.value, cursorPosition);
    input.setSelectionRange(newPos, newPos);
  }

  private findPreviousDigitPosition(
    value: string,
    cursorPosition: number
  ): number {
    let pos = cursorPosition - 1;
    while (pos > 0 && this.isSeparator(value[pos - 1])) {
      pos--;
    }
    return pos;
  }

  private isSeparator(char: string): boolean {
    return this.SEPARATORS.includes(char);
  }

  private format(digits: string): string {
    if (!digits) {
return '';
}

    if (digits.length <= this.MAX_DIGITS_CPF) {
      return this.formatCpf(digits);
    }
    return this.formatCnpj(digits);
  }

  // CPF: XXX.XXX.XXX-XX
  private formatCpf(digits: string): string {
    const n = digits.length;
    if (n <= 3) {
return digits;
}
    if (n <= 6) {
return `${digits.slice(0, 3)}.${digits.slice(3)}`;
}
    if (n <= 9) {
return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
}
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
  }

  // CNPJ: XX.XXX.XXX/XXXX-XX
  private formatCnpj(digits: string): string {
    const n = digits.length;
    if (n <= 2) {
return digits;
}
    if (n <= 5) {
return `${digits.slice(0, 2)}.${digits.slice(2)}`;
}
    if (n <= 8) {
return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
}
    if (n <= 12) {
return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
}
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
  }

  private countDigitsBeforePosition(value: string, position: number): number {
    let count = 0;
    for (let i = 0; i < position && i < value.length; i++) {
      if (this.DIGIT_REGEX.test(value[i])) {
        count++;
      }
    }
    return count;
  }

  private findCursorPosition(
    formattedValue: string,
    targetDigitCount: number
  ): number {
    let digitsFound = 0;
    for (let i = 0; i < formattedValue.length; i++) {
      if (this.DIGIT_REGEX.test(formattedValue[i])) {
        digitsFound++;
        if (digitsFound === targetDigitCount) {
          return i + 1;
        }
      }
    }
    return formattedValue.length;
  }

  private extractDigits(value: string): string {
    return value.replace(/\D/g, '').slice(0, this.MAX_DIGITS_CNPJ);
  }

  private shouldBlockInput(
    event: KeyboardEvent,
    input: HTMLInputElement
  ): boolean {
    const rawValue = this.extractDigits(input.value);
    const isDigit = /^\d$/.test(event.key);
    const hasSelection = input.selectionStart !== input.selectionEnd;
    return isDigit && rawValue.length >= this.MAX_DIGITS_CNPJ && !hasSelection;
  }

  private setCursorPosition(input: HTMLInputElement, position: number): void {
    setTimeout(() => {
      input.setSelectionRange(position, position);
    });
  }

  public getRawValue(): string {
    return this.extractDigits(this.el.nativeElement.value);
  }
}
